import { isDefined } from '@sgme/fp';
import { FormattedMessage } from 'components/shared/Intl';
import { TraceLink } from 'components/trace-link';
import { useQuoteWorkspaceContext } from 'contexts/quote-workspace.context';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useTraceLink } from 'hooks/use-trace-link';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { EnrichmentError as EnrichmentErrorType } from 'state/common-form/infos/infos.model';
import { dynamicFormTabRollbackedThunk } from 'state/dynamic/dynamicForm.thunks';
import { essentialFormTabRollbackedThunk } from 'state/essential/thunks';
import { commonFormActions } from 'state/forms/actions/form';
import { getFormInfos } from 'state/selectors';
import { staticFormTabRollbackedThunk } from 'state/static/staticForm.thunks';
import { workspaceTabRemovedThunk } from 'state/workspaces/workspace.thunks';

const useEnrichmentError = () => {
  const tabId = useAssertedActiveTabId();
  const workspace = useQuoteWorkspaceContext();
  const formInfo = useAppSelector(s => getFormInfos(s, workspace, tabId));
  const enrichmentError = formInfo?.enrichmentError as EnrichmentErrorType | undefined;
  const dispatch = useThunkDispatch();
  const onClose = () => {
    if (commonFormActions.isHandled(workspace)) {
      commonFormActions.formEnrichmentErrorDiscarded(workspace, tabId);
    }
    switch (workspace) {
      case 'essential':
        if (formInfo?.negotiationId) {
          dispatch(essentialFormTabRollbackedThunk(formInfo.negotiationId, tabId));
        }
        break;
      case 'static':
        if (formInfo?.negotiationId) {
          dispatch(staticFormTabRollbackedThunk(formInfo.negotiationId, tabId));
        }
        break;
      case 'dynamic':
        if (formInfo?.negotiationId) {
          dispatch(dynamicFormTabRollbackedThunk(formInfo.negotiationId, tabId));
        }
        break;
    }
  };
  const onRemoveTab = () => {
    if (commonFormActions.isHandled(workspace)) {
      commonFormActions.formEnrichmentErrorDiscarded(workspace, tabId);
    }
    dispatch(workspaceTabRemovedThunk(tabId, workspace));
  };
  return {
    shown: isDefined(formInfo?.enrichmentError),
    onClose,
    onRemoveTab,
    negotiationId: formInfo?.negotiationId,
    correlationId: enrichmentError?.correlationId,
    traceLink: useTraceLink(enrichmentError?.traceId),
  };
};
export function EnrichmentError() {
  const { shown, onClose, onRemoveTab, negotiationId, correlationId, traceLink } = useEnrichmentError();
  return (
    <Modal show={shown} data-e2e="error-details-modal" centered contentClassName="border border-danger">
      <ModalHeader>
        <ModalTitle as="h3" className="text-danger">
          <FormattedMessage id="enrichment.error.title" />
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <FormattedMessage id="enrichment.error.message" tagName="p" />
        {negotiationId && (
          <div data-e2e="error-details-negotiation-id">
            <FormattedMessage id="enrichment.error.negotiationId" />
            <b>{negotiationId}</b>
          </div>
        )}
        {correlationId && (
          <div data-e2e="error-details-correlation-id">
            <FormattedMessage id="enrichment.error.correlationId" />
            <b>{correlationId}</b>
          </div>
        )}
        {traceLink && (
          <div data-e2e="error-details-correlation-id">
            <TraceLink href={traceLink} />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button data-e2e="giveUpTabButton" className="btn btn-lg btn-flat-danger me-2" onClick={onRemoveTab}>
          <i className="icon me-2">delete</i>
          <FormattedMessage id="giveUpTab" />
        </button>
        <button data-e2e="rollbackButton" className="btn btn-lg btn-primary" onClick={onClose}>
          <FormattedMessage id="rollbackFormular" />
        </button>
      </ModalFooter>
    </Modal>
  );
}
